import { TIME } from '../constants'
import type { RegisteredTrigger, TriggerOptions } from './types'

// NotificationEventType будет импортирован из главного файла store
type NotificationEventType = string

/**
 * Базовые триггеры системы
 */

// ============================================
// Лимиты
// ============================================

export const limitTrigger: RegisteredTrigger = {
  name: 'limit',
  description: 'Универсальный лимит показов (за все время или за интервал)',
  fn: (options: TriggerOptions, max: number, interval?: number | 'hour' | 'day' | 'week') => {
    const { context, trackTrigger } = options
    
    if (!context.notificationId) {
      // Без notificationId возвращаем true (для примеров в админ панели)
      trackTrigger?.({
        type: 'limit',
        args: { max, interval },
        result: true,
        currentValue: 'N/A (no notificationId)'
      })
      return true
    }
    
    const state = context.notificationStates[context.notificationId]
    
    // Если интервал не указан - проверяем общее количество показов
    if (!interval) {
      const showCount = state?.showCount || 0
      const result = showCount < max
      trackTrigger?.({
        type: 'limit',
        args: { max },
        result,
        currentValue: showCount
      })
      return result
    }
    
    // Если интервал указан - проверяем частоту
    if (!state || !state.showTimestamps || state.showTimestamps.length === 0) {
      trackTrigger?.({
        type: 'limit',
        args: { max, interval },
        result: true,
        currentValue: 0
      })
      return true
    }
    
    // Конвертируем строковые интервалы в секунды
    let intervalSeconds: number
    if (typeof interval === 'string') {
      const intervals = { hour: TIME.HOUR, day: TIME.DAY, week: TIME.WEEK }
      intervalSeconds = intervals[interval]
    } else {
      intervalSeconds = interval
    }
    
    const intervalMs = intervalSeconds * 1000
    const cutoffTime = context.currentTime - intervalMs
    
    // Считаем показы за интервал (строго больше cutoffTime для правильного скользящего окна)
    const recentShows = state.showTimestamps.filter(
      (timestamp: number) => timestamp > cutoffTime
    )
    
    const result = recentShows.length < max
    trackTrigger?.({
      type: 'limit',
      args: { max, interval },
      result,
      currentValue: recentShows.length
    })
    return result
  }
}

export const limitSessionTrigger: RegisteredTrigger = {
  name: 'limitSession',
  description: 'Лимит показов за сессию (не персистентный)',
  fn: (options: TriggerOptions, max: number) => {
    const { context, trackTrigger } = options
    
    if (!context.notificationId) {
      // Без notificationId возвращаем true (для примеров в админ панели)
      trackTrigger?.({
        type: 'limitSession',
        args: { max },
        result: true,
        currentValue: 'N/A (no notificationId)'
      })
      return true
    }
    
    const sessionCount = context.sessionShowCounts?.[context.notificationId] || 0
    const result = sessionCount < max
    
    trackTrigger?.({
      type: 'limitSession',
      args: { max },
      result,
      currentValue: sessionCount
    })
    return result
  }
}

// ============================================
// Временные триггеры
// ============================================

export const waitTrigger: RegisteredTrigger = {
  name: 'wait',
  description: 'Подождать с последнего показа этого уведомления',
  fn: (options: TriggerOptions, seconds: number | 'hour' | 'day' | 'week') => {
    const { context, trackTrigger } = options
    
    if (!context.notificationId) {
      // Без notificationId возвращаем true (для примеров в админ панели)
      trackTrigger?.({
        type: 'wait',
        args: { seconds },
        result: true,
        currentValue: 'N/A (no notificationId)'
      })
      return true
    }
    
    // Конвертируем строки в секунды
    let waitSeconds: number
    if (typeof seconds === 'string') {
      const intervals = { hour: TIME.HOUR, day: TIME.DAY, week: TIME.WEEK }
      waitSeconds = intervals[seconds]
    } else {
      waitSeconds = seconds
    }
    
    const state = context.notificationStates[context.notificationId]
    const timeSince = state?.lastShownAt ? context.currentTime - state.lastShownAt : Infinity
    const result = !state || timeSince >= waitSeconds * 1000
    
    trackTrigger?.({
      type: 'wait',
      args: { seconds },
      result,
      currentValue: state?.lastShownAt ? Math.floor(timeSince / 1000) : '∞'
    })
    return result
  }
}

export const waitForTrigger: RegisteredTrigger = {
  name: 'waitFor',
  description: 'Подождать после конкретного уведомления',
  fn: (options: TriggerOptions, id: string, seconds: number | 'hour' | 'day' | 'week') => {
    const { context, trackTrigger } = options
    
    // Конвертируем строки в секунды
    let waitSeconds: number
    if (typeof seconds === 'string') {
      const intervals = { hour: TIME.HOUR, day: TIME.DAY, week: TIME.WEEK }
      waitSeconds = intervals[seconds]
    } else {
      waitSeconds = seconds
    }
    
    const state = context.notificationStates[id]
    const timeSince = state?.lastShownAt ? context.currentTime - state.lastShownAt : Infinity
    const result = !state || timeSince >= waitSeconds * 1000
    
    trackTrigger?.({
      type: 'waitFor',
      args: { id, seconds },
      result,
      currentValue: state?.lastShownAt ? Math.floor(timeSince / 1000) : '∞'
    })
    return result
  }
}

export const cooldownTrigger: RegisteredTrigger = {
  name: 'cooldown',
  description: 'Кулдаун между любыми уведомлениями',
  fn: (options: TriggerOptions, seconds: number) => {
    const { context, trackTrigger } = options
    
    const timeSince = context.lastShownAt ? context.currentTime - context.lastShownAt : Infinity
    const result = timeSince >= seconds * 1000
    
    trackTrigger?.({
      type: 'cooldown',
      args: { seconds },
      result,
      currentValue: Math.floor(timeSince / 1000)
    })
    return result
  }
}

export const pageLoadedTrigger: RegisteredTrigger = {
  name: 'pageLoaded',
  description: 'Прошло времени с загрузки страницы',
  fn: (options: TriggerOptions, seconds: number) => {
    const { context, trackTrigger } = options
    
    const timeSince = context.currentTime - context.pageLoadedAt
    const result = timeSince >= seconds * 1000
    
    trackTrigger?.({
      type: 'pageLoaded',
      args: { seconds },
      result,
      currentValue: Math.floor(timeSince / 1000)
    })
    return result
  }
}

// ============================================
// Активность
// ============================================

export const idleTrigger: RegisteredTrigger = {
  name: 'idle',
  description: 'Пользователь неактивен',
  fn: (options: TriggerOptions, seconds: number) => {
    const { context, trackTrigger } = options
    
    const idleTime = context.currentTime - context.lastActivityAt
    const result = idleTime >= seconds * 1000
    
    trackTrigger?.({
      type: 'idle',
      args: { seconds },
      result,
      currentValue: Math.floor(idleTime / 1000)
    })
    return result
  }
}

// ============================================
// URL и навигация
// ============================================

export const mainPageTrigger: RegisteredTrigger = {
  name: 'mainPage',
  description: 'Главная страница',
  fn: (options: TriggerOptions) => {
    const { context, trackTrigger } = options
    
    const result = /^\/$/.test(context.currentPath)
    
    trackTrigger?.({
      type: 'mainPage',
      args: {},
      result,
      currentValue: context.currentPath
    })
    return result
  }
}

export const urlTrigger: RegisteredTrigger = {
  name: 'url',
  description: 'URL содержит паттерн',
  fn: (options: TriggerOptions, pattern: string) => {
    const { context, trackTrigger } = options
    
    const fullUrl = context.currentPath + context.currentHash
    const result = fullUrl.includes(pattern)
    
    trackTrigger?.({
      type: 'url',
      args: { pattern },
      result,
      currentValue: fullUrl
    })
    return result
  }
}

// ============================================
// События
// ============================================

export const eventTrigger: RegisteredTrigger = {
  name: 'event',
  description: 'Произошло событие',
  fn: (options: TriggerOptions, eventType: NotificationEventType) => {
    const { context, trackTrigger } = options
    
    const result = context.recentEvents.some(event =>
      event.type === eventType &&
      (context.currentTime - event.timestamp) < 60000 // событие было в последнюю минуту
    )
    
    trackTrigger?.({
      type: 'event',
      args: { eventType },
      result,
      currentValue: result ? 'found' : 'not found'
    })
    return result
  }
}

// ============================================
// Кастомная логика
// ============================================

export const whenTrigger: RegisteredTrigger = {
  name: 'when',
  description: 'Кастомная функция',
  fn: (options: TriggerOptions, callback: () => boolean | Promise<boolean>) => {
    const { trackTrigger } = options
    
    const result = callback()
    
    trackTrigger?.({
      type: 'when',
      args: {},
      result: result instanceof Promise ? 'async' : result,
      currentValue: 'custom'
    })
    return result
  }
}

// ============================================
// Deprecated триггеры (для обратной совместимости)
// ============================================

export const afterLastShowAnyTrigger: RegisteredTrigger = {
  name: 'afterLastShowAny',
  description: '[DEPRECATED] Используйте cooldown()',
  fn: (options, seconds: number) => cooldownTrigger.fn(options, seconds)
}

export const afterLastShowOwnTrigger: RegisteredTrigger = {
  name: 'afterLastShowOwn',
  description: '[DEPRECATED] Используйте wait()',
  fn: (options, seconds: number) => {
    const { context, trackTrigger } = options
    
    if (!context.notificationId) {
      console.warn('afterLastShowOwn: notificationId is not provided')
      return true
    }
    
    // Вызываем wait триггер
    return waitTrigger.fn(options, seconds)
  }
}

export const afterLastShowTrigger: RegisteredTrigger = {
  name: 'afterLastShow',
  description: '[DEPRECATED] Используйте waitFor()',
  fn: (options, id: string, seconds: number) => waitForTrigger.fn(options, id, seconds)
}

export const afterPageLoadTrigger: RegisteredTrigger = {
  name: 'afterPageLoad',
  description: '[DEPRECATED] Используйте pageLoaded()',
  fn: (options, seconds: number) => pageLoadedTrigger.fn(options, seconds)
}

export const onIdleTrigger: RegisteredTrigger = {
  name: 'onIdle',
  description: '[DEPRECATED] Используйте idle()',
  fn: (options, seconds: number) => idleTrigger.fn(options, seconds)
}

export const onMainPageTrigger: RegisteredTrigger = {
  name: 'onMainPage',
  description: '[DEPRECATED] Используйте mainPage()',
  fn: (options) => mainPageTrigger.fn(options)
}

export const onUrlTrigger: RegisteredTrigger = {
  name: 'onUrl',
  description: '[DEPRECATED] Используйте url()',
  fn: (options, pattern: string) => urlTrigger.fn(options, pattern)
}

export const onEventTrigger: RegisteredTrigger = {
  name: 'onEvent',
  description: '[DEPRECATED] Используйте event()',
  fn: (options, eventType: NotificationEventType) => eventTrigger.fn(options, eventType)
}

export const onCallbackTrigger: RegisteredTrigger = {
  name: 'onCallback',
  description: '[DEPRECATED] Используйте when()',
  fn: (options, callback: () => boolean | Promise<boolean>) => whenTrigger.fn(options, callback)
}

export const maxShowsOwnTrigger: RegisteredTrigger = {
  name: 'maxShowsOwn',
  description: '[DEPRECATED] Используйте limit(max)',
  fn: (options, maxShows: number) => {
    const { context } = options
    
    if (!context.notificationId) {
      console.warn('maxShowsOwn: notificationId is not provided')
      return true
    }
    
    return limitTrigger.fn(options, maxShows)
  }
}

export const showLessOwnTrigger: RegisteredTrigger = {
  name: 'showLessOwn',
  description: '[DEPRECATED] Используйте limit(max, interval)',
  fn: (options, max: number, interval: number) => {
    const { context } = options
    
    if (!context.notificationId) {
      console.warn('showLessOwn: notificationId is not provided')
      return true
    }
    
    return limitTrigger.fn(options, max, interval)
  }
}

export const notBlockedOwnTrigger: RegisteredTrigger = {
  name: 'notBlockedOwn',
  description: '[DEPRECATED] Не нужен - проверяется автоматически',
  fn: (options: TriggerOptions) => {
    const { context, trackTrigger } = options
    
    if (!context.notificationId) {
      console.warn('notBlockedOwn: notificationId is not provided')
      return true
    }
    
    const result = !context.isNotificationBlocked(context.notificationId)
    
    trackTrigger?.({
      type: 'notBlockedOwn',
      args: {},
      result,
      currentValue: result ? 'not blocked' : 'blocked'
    })
    return result
  }
}
